import { useEffect } from "react";
import { useLoadingContext } from "../context/LoadingContext";

/**
 * Hook to manage loading overlay state with a minimum display time
 * @param {boolean|boolean[]|{isLoading: boolean, loadingReason: string}|{isLoading: boolean, loadingReason: string}[]} loadingStates - Single boolean or array of loading states to combine
 * @param {number} minLoadingTime - Minimum time in ms to show loading overlay (default: 500ms)
 * @returns {Object} Loading overlay state and controls
 */
const useLoadingOverlay = (loadingStates = [false], minLoadingTime = 0) => {
  const { setLoading, setLoadingReason, setIsLoadingVisible } =
    useLoadingContext();

  // loading states is an array of loading states
  // state can eb a signle boolnea, an array of booleans, an object, array of objects of a mix.
  // if its a n objectit will look like this { isLoading: true, loadingReason: "Loading..." }
  // this component will parse  the states an map them all to obejcts, even ifit was just a bool or an array of bools. final array will be of type as descrobed above.

  let parsedStates;

  if (Array.isArray(loadingStates)) {
    parsedStates = loadingStates.map((state) => {
      if (typeof state === "boolean") {
        return { isLoading: state, loadingReason: "", isLoadingVisible: true };
      } else if (typeof state === "object") {
        return {
          isLoading: state.isLoading,
          loadingReason: state.loadingReason,
          isLoadingVisible: state.isLoadingVisible ?? true,
        };
      } else if (Array.isArray(state)) {
        return state.map((s) => {
          if (typeof s === "boolean") {
            return { isLoading: s, loadingReason: "", isLoadingVisible: true };
          } else if (typeof s === "object") {
            return {
              isLoading: s.isLoading,
              loadingReason: s.loadingReason,
              isLoadingVisible: s.isLoadingVisible ?? true,
            };
          }
        });
      } else {
        return { isLoading: false, loadingReason: "", isLoadingVisible: false };
      }
    });
  } else {
    if (typeof loadingStates === "boolean") {
      parsedStates = [{ isLoading: loadingStates, loadingReason: "" }];
    } else if (typeof loadingStates === "object") {
      parsedStates = [loadingStates];
    } else {
      parsedStates = [
        { isLoading: false, loadingReason: "", isLoadingVisible: false },
      ];
    }
  }
  const isLoading = parsedStates.some((state) => state.isLoading);
  const activeLoadingReason =
    parsedStates.find((state) => state.isLoading)?.loadingReason ?? "";

  useEffect(() => {
    let timeoutId;
    if (isLoading) {
      timeoutId = setTimeout(() => {
        setLoading(true);
        setLoadingReason(activeLoadingReason);
        setIsLoadingVisible(true);
      }, minLoadingTime);
    } else {
      setLoading(false);
      setLoadingReason("");
      setIsLoadingVisible(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    isLoading,
    activeLoadingReason,
    minLoadingTime,
    setLoading,
    setLoadingReason,
    setIsLoadingVisible,
  ]);

  useEffect(() => {
    return () => {
      setLoading(false);
      setLoadingReason("");
      setIsLoadingVisible(false);
    };
  }, [setLoading, setLoadingReason, setIsLoadingVisible]);
};

export default useLoadingOverlay;
